import {
  Activity,
  BookOpen,
  Building2,
  Home,
  LifeBuoy,
  ScrollText,
  Settings,
  SlidersHorizontal,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';

import { LanguageToggle } from '@/components/layout/LanguageToggle';
import { UserMenu } from '@/components/layout/UserMenu';
import { cn } from '@/lib/utils';
import { useIsAdmin } from '@/stores/auth.store';

/**
 * Top navigation, no sidebar (05 §1.3) — the content area has to be wide enough
 * for the student × section matrix. RTL is handled by the document `dir`, so the
 * row simply reverses; nothing here hard-codes a side.
 *
 * ADMIN-only destinations are not rendered at all for a TA. The guard on the
 * route is what actually protects them; hiding the link is so a TA never follows
 * one into a wall.
 */

interface NavItem {
  to: string;
  labelKey: string;
  icon: LucideIcon;
  /** Only `/` must match exactly; the rest highlight for their whole subtree. */
  end?: boolean;
  adminOnly?: boolean;
  testId?: string;
}

const NAV_ITEMS: readonly NavItem[] = [
  { to: '/', labelKey: 'nav.home', icon: Home, end: true, testId: 'nav-home' },
  { to: '/courses', labelKey: 'nav.courses', icon: BookOpen, testId: 'nav-courses' },
  { to: '/cohorts', labelKey: 'nav.cohorts', icon: Users, testId: 'nav-cohorts' },
  /*
   * The ADMIN block. `/admin` is `end` because `/admin/audit` and
   * `/admin/guide-views` are its siblings, not its children — without it the
   * overview link would stay highlighted while the reader is on the audit log,
   * and "where am I" is the one job this bar has.
   */
  {
    to: '/admin',
    labelKey: 'nav.adminOverview',
    icon: Activity,
    end: true,
    adminOnly: true,
    testId: 'nav-admin-overview',
  },
  {
    to: '/admin/audit',
    labelKey: 'nav.auditLog',
    icon: ScrollText,
    adminOnly: true,
    testId: 'nav-admin-audit',
  },
  {
    to: '/admin/guide-views',
    labelKey: 'nav.guideViews',
    icon: LifeBuoy,
    adminOnly: true,
    testId: 'nav-admin-guide-views',
  },
  {
    to: '/admin/halls',
    labelKey: 'nav.halls',
    icon: Building2,
    adminOnly: true,
    testId: 'nav-halls',
  },
  {
    to: '/admin/settings',
    labelKey: 'nav.adminSettings',
    icon: SlidersHorizontal,
    adminOnly: true,
    testId: 'nav-admin-settings',
  },
  { to: '/settings', labelKey: 'nav.settings', icon: Settings, testId: 'nav-settings' },
];

export function TopBar() {
  const { t } = useTranslation();
  const isAdmin = useIsAdmin();

  const items = NAV_ITEMS.filter((item) => !item.adminOnly || isAdmin);

  return (
    <header className="sticky top-0 z-40 border-b bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80">
      <div className="mx-auto flex h-14 max-w-[1440px] items-center gap-4 px-4 sm:px-6">
        <NavLink
          to="/"
          className="flex shrink-0 items-center gap-2 font-semibold"
          aria-label={t('app.name')}
        >
          <span
            aria-hidden="true"
            className="grid size-7 place-items-center rounded-md bg-primary text-primary-foreground"
          >
            <svg viewBox="0 0 32 32" className="size-4" fill="none" aria-hidden="true">
              <path
                d="M9 8v16M23 8v16M9 16h14"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
              />
            </svg>
          </span>
          <span className="hidden text-base sm:inline">{t('app.name')}</span>
        </NavLink>

        <nav aria-label={t('nav.primary')} className="scroll-x flex items-center gap-1">
          {items.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end ?? false}
                data-testid={item.testId}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-1.5 whitespace-nowrap rounded-md px-2.5 py-1.5 text-sm transition-colors',
                    isActive
                      ? 'bg-secondary font-medium text-secondary-foreground'
                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                  )
                }
              >
                <Icon aria-hidden className="size-4" />
                <span>{t(item.labelKey)}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className="ms-auto flex items-center gap-1">
          <LanguageToggle />
          <UserMenu />
        </div>
      </div>
    </header>
  );
}
